import {
  buildMappedProjectParameterBindingGroups,
  buildProcessProjectParameterBindingGroups,
  MAPPED_PARAMETER_PROJECT_CATEGORIES,
} from '../../../shared/lib/hooks/useParameterAddition';
import type { ParameterTypeHint } from '../../../shared/lib/revit/projectParameterMutationPlan';
import { buildTextParameterTypeHints } from '../../../shared/lib/revit/wizardParameterDataTypes';
import {
  buildEquivalentRevitParameterNameLookup,
  getEquivalentRevitParameterNames,
} from '../../../shared/lib/revitParameterNames';
import {
  appendMappingParameterScopeRequirements,
  buildWizardSetupParameterScopeRequirements,
  type MappingParameterScopeRequirement,
} from '../../../shared/lib/mappingParameterScopeRequirements';
import { sanitizeMappingParameterReferenceName } from './mappingParameterReferenceName';
import type {
  MappingProjectParameterReference,
  MappingProjectReadinessInput,
} from './readiness/mappedParameterReferences';

// The keyed requirement machinery and the per-section builders live in
// shared/lib/mappingParameterScopeRequirements so the workflow-setup
// availability checks can verdict family scope through the same backend
// assessment rpc as mapping readiness. This module keeps only the
// readiness-specific selected-set builder, which depends on feature-local
// reference sanitizing.
export {
  buildAdditionalCategoryParameterScopeRequirements,
  buildConnectorParameterScopeRequirements,
  buildMappingParameterScopeRequirementKey,
  buildWizardSetupParameterScopeRequirements,
  selectRepairProjectBindings,
} from '../../../shared/lib/mappingParameterScopeRequirements';
export type {
  FamilyEmptyMatchPolicy,
  MappingParameterDestination,
  MappingParameterRepairKind,
  MappingParameterRequirement,
  MappingParameterRequirementTarget,
  MappingParameterScopeArea,
  MappingParameterScopeRequirement,
  MappingRepairProjectBinding,
} from '../../../shared/lib/mappingParameterScopeRequirements';

type SetupScopeInput = Pick<
  MappingProjectReadinessInput,
  'menuConfig' | 'connectorConfig' | 'boltConfig' | 'additionalCategoriesConfig'
>;

function normalizePart(value: string): string {
  return value.trim().toLowerCase();
}

function buildEquivalentNameLookup(parameterNames: string[]): Set<string> {
  const sanitizedNames = parameterNames
    .map(sanitizeMappingParameterReferenceName)
    .filter((name): name is string => name !== null);
  return buildEquivalentRevitParameterNameLookup(
    sanitizedNames
  );
}

function referenceMatchesNames(
  reference: MappingProjectParameterReference,
  lookup: Set<string>
): boolean {
  return getEquivalentRevitParameterNames(reference.name).some((name) =>
    lookup.has(normalizePart(name))
  );
}

export function buildSelectedMappingParameterScopeRequirements(
  input: SetupScopeInput,
  references: MappingProjectParameterReference[],
  mappedParameterNamesRequiringProjectBinding: string[] = [],
  mappedParameterTypeHints: ParameterTypeHint[] = []
): MappingParameterScopeRequirement[] {
  const requirements = buildWizardSetupParameterScopeRequirements(input);
  const connectorParameterNames = references
    .filter((reference) => reference.sources.includes('connector'))
    .map((reference) => reference.name);
  const appendProjectGroups = (
    area: 'process' | 'project',
    categories: string[],
    source: 'process' | 'project'
  ) => {
    const names = references
      .filter((reference) =>
        reference.repairKinds.includes('processProject') && reference.sources.includes(source)
      )
      .map((reference) => reference.name);
    buildProcessProjectParameterBindingGroups({
      parameterNames: names,
      connectorParameterNames,
      connectorBindingKind: input.connectorConfig.parameterBindingKind,
    }).forEach((group) => appendMappingParameterScopeRequirements(requirements, {
      parameterNames: group.parameterNames,
      area,
      repairKind: 'processProject',
      destination: 'project',
      bindingKind: group.bindingKind,
      target: { kind: 'projectCategories', categories },
      parameterTypeHints: buildTextParameterTypeHints(group.parameterNames),
    }));
  };
  appendProjectGroups('process', ['Piping Systems'], 'process');
  appendProjectGroups('project', ['Project Information'], 'project');

  const dedicatedKinds = new Set<MappingParameterScopeRequirement['repairKind']>([
    'connector', 'bolt', 'additional',
  ]);
  const mappedLookup = buildEquivalentNameLookup(mappedParameterNamesRequiringProjectBinding);
  const mappedNames = references
    .filter((reference) =>
      reference.repairKinds.includes('mapping') &&
      !reference.repairKinds.some((kind) => dedicatedKinds.has(kind)) &&
      referenceMatchesNames(reference, mappedLookup)
    )
    .map((reference) => reference.name);
  buildMappedProjectParameterBindingGroups({
    parameterNames: mappedNames,
    preferredBindingKind: input.connectorConfig.parameterBindingKind,
  }).forEach((group) => appendMappingParameterScopeRequirements(requirements, {
    parameterNames: group.parameterNames,
    area: 'mapping',
    repairKind: 'mapping',
    destination: 'project',
    bindingKind: group.bindingKind,
    target: { kind: 'projectCategories', categories: MAPPED_PARAMETER_PROJECT_CATEGORIES },
    parameterTypeHints: mappedParameterTypeHints,
  }));

  return Array.from(new Map(requirements.map((item) => [item.key, item])).values());
}
